// 🔧 AuthContext.js
// ✅ Purpose: Centralized auth state using Firebase + React Context
// ✅ Features:
// - Tracks Firebase auth user
// - Fetches Firestore user profile (/users/{uid})
// - Merges email, name, role, etc. into single `user` object
// - Makes user and authLoading available via useAuth()

import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { logger } from './utils/logger';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
  
      if (firebaseUser) {
        try {
          const userRef = doc(db, "users", firebaseUser.uid);
          const userSnap = await getDoc(userRef);
          const profile = userSnap.exists() ? userSnap.data() : {};
  
          const fullUser = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            emailVerified: firebaseUser.emailVerified,
            ...profile,
          };
  
          setUser(fullUser);

          logger.log("🔐 Auth User Loaded", {
            email: firebaseUser.email,
            emailVerified: firebaseUser.emailVerified,
          });

          logger.log("🔐 Auth Profile Loaded", {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            role: profile.role,
            emailVerified: firebaseUser.emailVerified,
          });

        } catch (err) {
          const offline =
            err?.code === "unavailable" || err?.code === "firestore/unavailable";
          const log = offline ? logger.warn : logger.error;

          log("❌ Failed to fetch Firestore user profile:", {
            value: err
          });
          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
          });
        }
      } else {
        setUser(null);
      }
  
      setAuthLoading(false);
    });
  
    return () => unsubscribe();
  }, []);
  

  return (
    <AuthContext.Provider value={{ user, authLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
